import { ApiResponse, BaseCreateResponse, BaseInfo, BaseResponse, baseUrl, deleteWithAuth, EmptyRequest, getWithAuth, Guid, ListRespone, patchWithAuth, postWithAuth, postWithAuthWitoutParams } from "./baseApi"
import { ChannelStream, CreateChannelStreamRequest, EnableSlotRequest, ReorderItemPair, TimeSlotModel } from "./channelsApi"
import { MediaType } from "./mediaApi"

export type ScheduledPriorityList = {
    id: string
    name : string
    channels: BaseInfo[],
    status: ScheduledPriorityListStatus,
}

export type ScheduledPriorityListStream = {
    id: Guid,
    name: string,
    startTime: string,
    endTime: string,
    isNestedListEnabled: boolean,
    showNestedListEachMinutes: number,
    isTriggeringSkin: boolean,
    nestedPlayListId: Guid,
    tvChannelId: Guid | null,
    tvChannelName: string,
    includeTagIds: BaseInfo[],
    excludeTagIds: BaseInfo[],
    useORRelationshipForIncludeTags: boolean,
    postRollPlaylistId: Guid,
    preRollPlaylistId: Guid,
    series: string,
    season: string,
    episode: string,
    iabCat: string,
    genre: string,
    rating: string,
    title: string,
    rtmpUrl: string,
    sourceType: SourceType
}

export enum SourceType {
    CapturedStream,
    RTMPStream
}

export type CreateScheduledPriorityListStreamRequest = {
    name: string,
    startTime: string,
    endTime: string,
    isNestedListEnabled: boolean,
    showNestedListEachMinutes: number,
    nestedPlayListId: Guid | null,
    tvChannelId: Guid | null,
    includeTagIds: Guid[],
    excludeTagIds: Guid[],
    useORRelationshipForIncludeTags: boolean,
    postRollPlaylistId: Guid | null,
    preRollPlaylistId: Guid | null,
    series: string,
    season: string,
    episode: string,
    iabCat: string,
    genre: string,
    rating: string,
    title: string,
    rtmpUrl: string,
    sourceType: SourceType
}


export type CreateScheduledPriorityListRequest = {
    name : string,
    channelIds: string[],
}

export type UpdateScheduledPriorityListRequest = {
    id: Guid,
    name : string,
    channelIds: string[],
}

export type ScheduledPriorityListMedia = {
    id: Guid,
    name: string,
    url: string,
    order: number,
    durationMs: number,
    type: MediaType,
    startTime: string,
    endTime: string,
    isTriggeringSkin: boolean,
    schedule: PriorityListMediaSchedule[]
}

export type ScheduledPriorityListAddMediaRequest = {
    mediaIds: Guid[]
}

export type ScheduledPriorityListReorderMedia = {
    reorderItems: ReorderItemPair[]
}

export enum ScheduledPriorityListStatus {
    Active = 0,
    Inactive = 1
}


export interface PriorityListMediaFlydatesRequest {
    startTime: string | null,
    endTime: string | null
}

export interface PriorityListMediaFlytimeResponse extends PriorityListMediaFlydatesRequest {
    id: Guid
}

export interface CreateNestedPlayListRequest {
    name: string,
    isUseRandomOrder: boolean
}

export interface UpdateNestedPlayListRequest {
    name: string,
    isUseRandomOrder: boolean
}

export interface NestedPlayList {
    id: Guid,
    name: string
}

export interface PriorityListMediaSchedule {
    id: Guid,
    name: string,
    start: string,
    end: string,
    startTime: string,
    endTime: string
}

export type PriorityListMediaScheduleRequest = Omit<PriorityListMediaSchedule, 'id'>

export type WeekDays = {
    monday: boolean,
    tuesday: boolean,
    wednesday: boolean,
    thursday: boolean,
    friday:boolean,
    saturday: boolean,
    sunday: boolean
} 

export type RecurrentPriorityListStream = {
    id: Guid,
    name: string,
    startDate: string,
    endDate: string | null,
    startTime: string,
    endTime: string,
    monday: boolean,
    tuesday: boolean,
    wednesday: boolean,
    thursday: boolean,
    friday: boolean,
    saturday: boolean,
    sunday: boolean,
    isNestedListEnabled: boolean,
    showNestedListEachMinutes: number,
    isTriggeringSkin: boolean,
    nestedPlayListId: Guid | null,
    tvChannelId: Guid | null,
    tvChannelName: string,
    includeTagIds: BaseInfo[],
    excludeTagIds: BaseInfo[],
    useORRelationshipForIncludeTags: boolean,
    postRollPlayListId: Guid,
    preRollPlayListId: Guid,
    series: string,
    season: string,
    episode: string,
    iabCat: string,
    genre: string,
    rating: string,
    title: string,
    rtmpUrl: string,
    sourceType: SourceType
}

export type CreateRecurrentPriorityListStreamRequest = {
    name: string,
    startDate: string,
    endDate: string | null,
    startTime: string,
    endTime: string,
    monday: boolean,
    tuesday: boolean,
    wednesday: boolean,
    thursday: boolean,
    friday: boolean,
    saturday: boolean,
    sunday: boolean,
    isNestedListEnabled: boolean,
    showNestedListEachMinutes: number,
    nestedPlayListId: Guid | null,
    tvChannelId: Guid | null,
    includeTagIds: Guid[],
    excludeTagIds: Guid[],
    useORRelationshipForIncludeTags: boolean,
    postRollPlayListId: Guid | null,
    preRollPlayListId: Guid | null,
    series: string,
    season: string,
    episode: string,
    iabCat: string,
    genre: string,
    rating: string,
    title: string,
    rtmpUrl: string,
    sourceType: SourceType
}

export const getScheduledPriorityLists = async() : Promise<ApiResponse<ListRespone<ScheduledPriorityList>>> => {
    return await getWithAuth<ListRespone<ScheduledPriorityList>>({url : `${baseUrl}scheduled-priority-list`});
}

export const createScheduledPriorityList = async(request : CreateScheduledPriorityListRequest) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<CreateScheduledPriorityListRequest,BaseCreateResponse>(`${baseUrl}scheduled-priority-list`, request);
}

export const updateScheduledPriorityList = async(request : UpdateScheduledPriorityListRequest) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<UpdateScheduledPriorityListRequest,BaseResponse>(`${baseUrl}scheduled-priority-list`, request);
}

export const getScheduledListTimeSlots = async (id: Guid) : Promise<ApiResponse<ListRespone<TimeSlotModel>>> => {
    return await getWithAuth<ListRespone<TimeSlotModel>>({url: `${baseUrl}scheduled-priority-list/${id}/timeslots`}); 
}

export const enableScheduledListTimeSlot = async (timeSlot: EnableSlotRequest, channelId: Guid) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<EnableSlotRequest, BaseCreateResponse>(`${baseUrl}scheduled-priority-list/${channelId}/timeslots`, timeSlot); 
}

export const removeScheduledListTimeSlots = async (id: Guid, timeSlotId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await deleteWithAuth<BaseResponse>(`${baseUrl}scheduled-priority-list/${id}/timeslots`, timeSlotId); 
}

export const getScheduledPriorityListMedia = async (id: Guid) : Promise<ApiResponse<ListRespone<ScheduledPriorityListMedia>>> => {
    return await getWithAuth<ListRespone<ScheduledPriorityListMedia>>({url : `${baseUrl}scheduled-priority-list/${id}/media`});
}

export const enableScheduledList = async(id: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuthWitoutParams<BaseResponse>(`${baseUrl}scheduled-priority-list/${id}/enable`);
}

export const disableScheduledList = async(id: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuthWitoutParams<BaseResponse>(`${baseUrl}scheduled-priority-list/${id}/disable`);
}

export const getScheduledListStreams = async (listId: Guid) : Promise<ApiResponse<ListRespone<ScheduledPriorityListStream>>> => {
    return await getWithAuth<ListRespone<ScheduledPriorityListStream>>({url: `${baseUrl}scheduled-priority-list/${listId}/streams`}); 
}

export const createScheduledListStream = async (request: CreateScheduledPriorityListStreamRequest, listId: Guid) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<CreateScheduledPriorityListStreamRequest, BaseCreateResponse>(`${baseUrl}scheduled-priority-list/${listId}/streams`, request); 
}

export const updateScheduledListStream = async (request: CreateScheduledPriorityListStreamRequest, streamId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<CreateScheduledPriorityListStreamRequest, BaseResponse>(`${baseUrl}scheduled-priority-list/streams/${streamId}`, request); 
}

export const deleteScheduledListStream = async (streamId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await deleteWithAuth<BaseCreateResponse>(`${baseUrl}scheduled-priority-list/streams`, streamId); 
}

export const deleteScheduledList = async (id: Guid) : Promise<ApiResponse<BaseResponse>> =>{
    return await deleteWithAuth<BaseResponse>(`${baseUrl}scheduled-priority-list`, id)
}

export const enableSkinPriorityListMedia = async (priorityListId : Guid, nestedPlaylistId: Guid, mediaId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}scheduled-priority-list/${priorityListId}/nested-playlist/${nestedPlaylistId}/media/${mediaId}/enable-skin`, {}); 
}

export const disableSkinPriorityListMedia = async (priorityListId : Guid, nestedPlaylistId: Guid, mediaId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}scheduled-priority-list/${priorityListId}/nested-playlist/${nestedPlaylistId}/media/${mediaId}/disable-skin`, {}); 
}

export const enableSkinPriorityListStream = async (id: Guid, streamId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}scheduled-priority-list/${id}/streams/${streamId}/enable-skin`, {}); 
}

export const disableSkinPriorityListStream = async (id: Guid, streamId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}scheduled-priority-list/${id}/streams/${streamId}/disable-skin`, {}); 
}


//ADD NESTED PLAYLIST
export const getScheduledPriorityNestedPlayLists = async(priorityListId: Guid) : Promise<ApiResponse<ListRespone<NestedPlayList>>> => {
    return await getWithAuth<ListRespone<NestedPlayList>>({url : `${baseUrl}scheduled-priority-list/${priorityListId}/nested-playlist`});
}

export const createScheduledPriorityNestedPlayList = async(priorityListId : Guid, request : CreateNestedPlayListRequest) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<CreateNestedPlayListRequest,BaseCreateResponse>(`${baseUrl}scheduled-priority-list/${priorityListId}/nested-playlist`, request);
}

export const updateScheduledPriorityNestedPlayList = async(priorityListId : Guid, nestedPlayListId: Guid, request : UpdateNestedPlayListRequest) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<UpdateNestedPlayListRequest,BaseResponse>(`${baseUrl}scheduled-priority-list/${priorityListId}/nested-playlist/${nestedPlayListId}`, request);
}

export const deleteScheduledPriorityNestedPlayList = async (priorityListId : Guid, nestedPlaylistId: Guid) : Promise<ApiResponse<BaseResponse>> =>{
    return await deleteWithAuth<BaseResponse>(`${baseUrl}scheduled-priority-list/${priorityListId}/nested-playlist`, nestedPlaylistId)
}

export const getScheduledPriorityNestedPlayListMedias = async(priorityListId : Guid, nestedPlaylistId: Guid) : Promise<ApiResponse<ListRespone<ScheduledPriorityListMedia>>> => {
    return await getWithAuth<ListRespone<ScheduledPriorityListMedia>>({url : `${baseUrl}scheduled-priority-list/${priorityListId}/nested-playlist/${nestedPlaylistId}/media`});
}

export const addMediaToScheduledPriorityNestedPlayList= async (priorityListId : Guid, nestedPlaylistId: Guid, request: ScheduledPriorityListAddMediaRequest) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<ScheduledPriorityListAddMediaRequest, BaseResponse>(`${baseUrl}scheduled-priority-list/${priorityListId}/nested-playlist/${nestedPlaylistId}/media`, request); 
}

export const deleteScheduledPriorityNestedPlayListMedia = async (priorityListId : Guid, nestedPlaylistId: Guid, mediaId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await deleteWithAuth<BaseResponse>(`${baseUrl}scheduled-priority-list/${priorityListId}/nested-playlist/${nestedPlaylistId}/media`, mediaId); 
}

export const reorderScheduledPriorityNestedPlayListMedia = async (priorityListId : Guid, nestedPlaylistId: Guid, request: ScheduledPriorityListReorderMedia) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<ScheduledPriorityListReorderMedia, BaseResponse>(`${baseUrl}scheduled-priority-list/${priorityListId}/nested-playlist/${nestedPlaylistId}/media/reorder`, request); 
}

export const getPriorityListMediaSchedule = async (priorityListId : Guid, nestedPlaylistId: Guid, mediaId:Guid): Promise<ApiResponse<ListRespone<PriorityListMediaSchedule>>> => {
    return await getWithAuth<ListRespone<PriorityListMediaSchedule>>({url :`${baseUrl}scheduled-priority-list/${priorityListId}/nested-playlist/${nestedPlaylistId}/media/${mediaId}/schedule`});
}

export const addPriorityListMediaSchedule = async (request: PriorityListMediaScheduleRequest, priorityListId : Guid, nestedPlaylistId: Guid, mediaId:Guid): Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<PriorityListMediaScheduleRequest,BaseCreateResponse>(`${baseUrl}scheduled-priority-list/${priorityListId}/nested-playlist/${nestedPlaylistId}/media/${mediaId}/schedule`, request);
}

export const deletePriorityListMediaSchedule = async (priorityListId : Guid, nestedPlaylistId: Guid, mediaId:Guid, periodId: Guid): Promise<ApiResponse<BaseResponse>> => {
    return await deleteWithAuth<BaseResponse>(`${baseUrl}scheduled-priority-list/${priorityListId}/nested-playlist/${nestedPlaylistId}/media/${mediaId}/schedule`, periodId);
}

//Reccurent Streams
export const getScheduledListRecurrentStreams = async (listId: Guid) : Promise<ApiResponse<ListRespone<RecurrentPriorityListStream>>> => {
    return await getWithAuth<ListRespone<RecurrentPriorityListStream>>({url: `${baseUrl}scheduled-priority-list/${listId}/recurrent-streams`}); 
}

export const createScheduledListRecurrentStream = async (request: CreateRecurrentPriorityListStreamRequest, listId: Guid) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<CreateRecurrentPriorityListStreamRequest, BaseCreateResponse>(`${baseUrl}scheduled-priority-list/${listId}/recurrent-streams`, request); 
}

export const updateScheduledListRecurrentStream = async (request: CreateRecurrentPriorityListStreamRequest, listId: Guid, streamId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<CreateRecurrentPriorityListStreamRequest, BaseResponse>(`${baseUrl}scheduled-priority-list/${listId}/recurrent-streams/${streamId}`, request); 
}

export const deleteScheduledListRecurrentStream = async ( listId: Guid, streamId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await deleteWithAuth<BaseCreateResponse>(`${baseUrl}scheduled-priority-list/${listId}/recurrent-streams`, streamId); 
}

export const enableSkinScheduledListRecurrentStream = async (id: Guid, streamId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}scheduled-priority-list/${id}/recurrent-streams/${streamId}/enable-skin`, {}); 
}

export const disableSkinScheduledListRecurrentStream = async (id: Guid, streamId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<EmptyRequest, BaseResponse>(`${baseUrl}scheduled-priority-list/${id}/recurrent-streams/${streamId}/disable-skin`, {}); 
}
